import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Home, Settings, LogOut, Moon, Sun, ChevronLeft, ChevronRight, Menu, Image, Plus } from 'lucide-react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import Button from './ui/Button';
import SmileDesignLogo from './SmileDesignLogo';

interface SidebarProps {
    onGoHome: () => void;
}

const Sidebar: React.FC<SidebarProps> = ({ onGoHome }) => {
    const [isCollapsed, setIsCollapsed] = useState(false);
    const [isMobile, setIsMobile] = useState(false);
    const [isMobileOpen, setIsMobileOpen] = useState(false);
    const navigate = useNavigate();
    const location = useLocation();
    const { user, logout } = useAuth();

    useEffect(() => {
        const checkMobile = () => {
            setIsMobile(window.innerWidth < 768);
            if (window.innerWidth < 768) {
                setIsCollapsed(false); // Reset collapse state on mobile as we use open/close
            }
        };
        checkMobile();
        window.addEventListener('resize', checkMobile);
        return () => window.removeEventListener('resize', checkMobile);
    }, []);

    const menuItems = [
        { icon: <Plus size={20} />, label: 'Start New Simulation', path: '/app' },
        { icon: <Home size={20} />, label: 'Dashboard', path: '/dashboard' },
        { icon: <Image size={20} />, label: 'Showcase Creator', path: '/showcase' },
        { icon: <Settings size={20} />, label: 'Account Settings', path: '/dashboard', state: { openSettingsModal: true } },
    ];

    const sidebarContent = (
        <div className="flex flex-col h-full">
            {/* Toggle Button (Desktop) */}
            {!isMobile && (
                <button
                    onClick={() => setIsCollapsed(!isCollapsed)}
                    className="absolute -right-3 top-8 w-6 h-6 bg-white dark:bg-zinc-800 border border-zinc-200 dark:border-zinc-700 rounded-full flex items-center justify-center text-zinc-500 hover:text-teal-500 transition-colors shadow-sm z-50"
                >
                    {isCollapsed ? <ChevronRight size={14} /> : <ChevronLeft size={14} />}
                </button>
            )}

            {/* Close Button (Mobile) */}
            {isMobile && (
                <button
                    onClick={() => setIsMobileOpen(false)}
                    className="absolute right-4 top-4 p-2 text-zinc-500 hover:text-zinc-900 dark:text-zinc-400 dark:hover:text-white"
                >
                    <ChevronLeft size={24} />
                </button>
            )}

            {/* Enhanced Header with Logo & Credits */}
            <div className="p-4 border-b border-zinc-200 dark:border-zinc-800">
                {/* Logo Section */}
                <div className="flex items-center mb-3 overflow-hidden">
                    <motion.div
                        animate={{
                            opacity: isCollapsed && !isMobile ? 0 : 1,
                            width: isCollapsed && !isMobile ? 0 : '100%'
                        }}
                        className="overflow-hidden w-full"
                    >
                        <img
                            src="/images/Logo.webp"
                            alt="FuturaSmile Logo"
                            className="w-full h-auto object-contain"
                        />
                    </motion.div>

                    {/* Collapsed state: Show logo icon */}
                    {isCollapsed && !isMobile && (
                        <div className="w-10 h-10 flex items-center justify-center">
                            <SmileDesignLogo />
                        </div>
                    )}
                </div>

                {/* Credits Counter - Professional White Design */}
                <motion.div
                    animate={{
                        opacity: isCollapsed && !isMobile ? 0.6 : 1,
                        scale: isCollapsed && !isMobile ? 0.9 : 1
                    }}
                    className={`
                        relative overflow-hidden
                        bg-white/[0.03] dark:bg-white/[0.02]
                        backdrop-blur-xl
                        border border-white/[0.08]
                        rounded-2xl
                        hover:border-white/[0.12]
                        transition-all duration-500
                        group
                        ${isCollapsed && !isMobile ? 'p-2.5' : 'p-4'}
                    `}
                >
                    {/* Subtle top highlight */}
                    <div className="absolute top-0 left-0 right-0 h-[1px] bg-gradient-to-r from-transparent via-white/10 to-transparent" />

                    {/* Refined shimmer effect */}
                    <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/[0.02] to-transparent translate-x-[-200%] group-hover:translate-x-[200%] transition-transform duration-1500 ease-in-out" />

                    <div className={`relative flex items-center ${isCollapsed && !isMobile ? 'flex-col' : 'justify-between'} gap-3`}>
                        {/* Credits Display */}
                        <div className={`flex items-center gap-2.5 ${isCollapsed && !isMobile ? 'flex-col' : ''}`}>
                            <div className="p-2 bg-white/[0.06] rounded-xl border border-white/[0.1] shadow-sm">
                                <motion.div
                                    whileHover={{ rotate: 360 }}
                                    transition={{ duration: 0.7, ease: "easeInOut" }}
                                >
                                    <span className="material-symbols-outlined text-base">paid</span>
                                </motion.div>
                            </div>

                            <div className={`${isCollapsed && !isMobile ? 'text-center' : ''} flex-1`}>
                                <div className="flex items-baseline gap-1.5">
                                    <motion.span
                                        key={user?.credits || 0}
                                        initial={{ scale: 1.08 }}
                                        animate={{ scale: 1 }}
                                        transition={{ duration: 0.4, ease: 'easeOut' }}
                                        className={`font-bold ${isCollapsed && !isMobile ? 'text-base' : 'text-2xl'} text-white tracking-tight`}
                                        style={{ letterSpacing: '-0.02em' }}
                                    >
                                        {user?.credits || 0}
                                    </motion.span>
                                    {!isCollapsed || isMobile ? (
                                        <span className="text-xs text-zinc-400 dark:text-zinc-500 font-medium">Credits</span>
                                    ) : null}
                                </div>

                                {(!isCollapsed || isMobile) && (
                                    <div className="text-[10px] text-zinc-400 dark:text-zinc-500 font-medium mt-0.5">
                                        Est. Value: ${((user?.credits || 0) * 0.8).toFixed(2)}
                                    </div>
                                )}
                            </div>
                        </div>

                        {/* Enhanced Buy Credits Button */}
                        {(!isCollapsed || isMobile) && (
                            <motion.button
                                whileHover={{ scale: 1.05 }}
                                whileTap={{ scale: 0.95 }}
                                onClick={() => {
                                    navigate('/dashboard', { state: { openPurchaseModal: true } });
                                }}
                                className="p-2.5 bg-white/[0.08] hover:bg-white/[0.12] text-white rounded-xl border border-white/[0.1] hover:border-white/[0.15] transition-all duration-300"
                                aria-label="Purchase additional credits"
                            >
                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M12 4v16m8-8H4" />
                                </svg>
                            </motion.button>
                        )}
                    </div>

                    {/* Refined Low Credits Warning */}
                    {(user?.credits || 0) < 10 && (user?.credits || 0) > 0 && (!isCollapsed || isMobile) && (
                        <motion.div
                            initial={{ opacity: 0, height: 0 }}
                            animate={{ opacity: 1, height: 'auto' }}
                            transition={{ duration: 0.3 }}
                            className="mt-3 pt-3 border-t border-amber-500/20"
                        >
                            <div className="flex items-center gap-2 px-2 py-1.5 bg-amber-500/5 rounded-lg border border-amber-500/15">
                                <svg className="w-3.5 h-3.5 flex-shrink-0 text-amber-500" fill="currentColor" viewBox="0 0 20 20">
                                    <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                                </svg>
                                <span className="text-[10px] font-semibold text-amber-500">Credit Balance Low</span>
                            </div>
                        </motion.div>
                    )}

                    {/* Refined Zero Credits Alert */}
                    {(user?.credits || 0) === 0 && (!isCollapsed || isMobile) && (
                        <motion.div
                            initial={{ opacity: 0, height: 0 }}
                            animate={{ opacity: 1, height: 'auto' }}
                            transition={{ duration: 0.3 }}
                            className="mt-3 pt-3 border-t border-red-500/20"
                        >
                            <div className="flex items-center gap-2 px-2 py-1.5 bg-red-500/5 rounded-lg border border-red-500/15">
                                <motion.svg
                                    animate={{ scale: [1, 1.15, 1] }}
                                    transition={{ repeat: Infinity, duration: 2, ease: "easeInOut" }}
                                    className="w-3.5 h-3.5 flex-shrink-0 text-red-500"
                                    fill="currentColor"
                                    viewBox="0 0 20 20"
                                >
                                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                                </motion.svg>
                                <span className="text-[10px] font-semibold text-red-500">No Credits Remaining</span>
                            </div>
                        </motion.div>
                    )}
                </motion.div>
            </div>

            {/* Navigation */}
            <nav className="flex-1 px-3 py-4 space-y-1">
                {menuItems.map((item) => {
                    const isActive = location.pathname === item.path;
                    return (
                        <button
                            key={item.path}
                            onClick={() => {
                                // @ts-ignore - state is not on the standard item types yet but we added it
                                navigate(item.path, { state: item.state });
                                if (isMobile) setIsMobileOpen(false);
                            }}
                            className={`
                w-full flex items-center gap-3 px-3 py-3 rounded-xl transition-all duration-200 group
                ${isActive
                                    ? 'bg-zinc-100 dark:bg-zinc-800 text-zinc-900 dark:text-white'
                                    : 'text-zinc-500 dark:text-zinc-400 hover:bg-zinc-100 dark:hover:bg-zinc-800 hover:text-zinc-900 dark:hover:text-white'
                                }
              `}
                        >
                            <div className={`
                ${isActive ? 'text-zinc-900 dark:text-white' : 'text-zinc-400 group-hover:text-zinc-600 dark:group-hover:text-zinc-300'}
              `}>
                                {item.icon}
                            </div>
                            <motion.span
                                animate={{ opacity: isCollapsed && !isMobile ? 0 : 1, width: isCollapsed && !isMobile ? 0 : 'auto' }}
                                className="font-medium whitespace-nowrap overflow-hidden"
                            >
                                {item.label}
                            </motion.span>
                            {isActive && (!isCollapsed || isMobile) && (
                                <motion.div
                                    layoutId="activeIndicator"
                                    className="ml-auto w-1.5 h-1.5 rounded-full bg-zinc-900 dark:bg-white"
                                />
                            )}
                        </button>
                    );
                })}
            </nav>

            {/* Footer Actions */}


            {/* Footer Actions */}
            <div className="p-3 border-t border-zinc-200 dark:border-zinc-800 space-y-1">
                <button
                    onClick={onGoHome}
                    className="w-full flex items-center gap-3 px-3 py-3 rounded-xl text-zinc-500 dark:text-zinc-400 hover:bg-zinc-100 dark:hover:bg-zinc-800 hover:text-zinc-900 dark:hover:text-white transition-all"
                >
                    <LogOut size={20} />
                    <motion.span
                        animate={{ opacity: isCollapsed && !isMobile ? 0 : 1, width: isCollapsed && !isMobile ? 0 : 'auto' }}
                        className="font-medium whitespace-nowrap overflow-hidden"
                    >
                        Return to Website
                    </motion.span>
                </button>
            </div>
        </div >
    );

    return (
        <>
            {/* Mobile Trigger */}
            {isMobile && !isMobileOpen && (
                <button
                    onClick={() => setIsMobileOpen(true)}
                    className="fixed top-4 left-4 z-40 p-2 bg-white dark:bg-zinc-900 rounded-lg shadow-lg border border-zinc-200 dark:border-zinc-800 text-zinc-700 dark:text-zinc-200"
                >
                    <Menu size={24} />
                </button>
            )}

            {/* Mobile Overlay */}
            <AnimatePresence>
                {isMobile && isMobileOpen && (
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        onClick={() => setIsMobileOpen(false)}
                        className="fixed inset-0 bg-black/50 backdrop-blur-sm z-40"
                    />
                )}
            </AnimatePresence>

            {/* Sidebar Container */}
            <motion.div
                initial={false}
                animate={{
                    width: isMobile ? 280 : (isCollapsed ? 80 : 240),
                    x: isMobile ? (isMobileOpen ? 0 : -280) : 0
                }}
                transition={{ duration: 0.3, ease: "easeInOut" }}
                className={`
          h-full bg-white/80 dark:bg-zinc-900/80 backdrop-blur-xl border-r border-zinc-200 dark:border-zinc-800 flex-shrink-0
          ${isMobile ? 'fixed inset-y-0 left-0 z-50 shadow-2xl' : 'relative z-30'}
        `}
            >
                {sidebarContent}
            </motion.div>
        </>
    );
};

export default Sidebar;
